import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  DatePicker,
  Select,
  Upload,
  Button,
  Row,
  Col,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { createDoctor, updateDoctor } from "../../../http/doctorAPI";
import { fetchCategories } from "../../../http/categoryAPI";
import { $host } from "../../../http";

const { TextArea } = Input;
const { Option } = Select;

const DoctorForm = ({ mode = "add", doctor = null, onSuccess }) => {
  const [categories, setCategories] = useState([]); 
  const [fileList, setFileList] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [form] = Form.useForm();

  // Load categories
  useEffect(() => {
    fetchCategories().then(setCategories).catch(console.error);
  }, []);

  // Prefill form when editing
  useEffect(() => {
    if (doctor) {
      form.setFieldsValue({
        ...doctor,
        birthDate: doctor.birthDate ? dayjs(doctor.birthDate) : null,
        categoryId: doctor.categoryId || undefined,
      });

      if (doctor.img_main) {
        setPreviewUrl($host.defaults.baseURL + `photos/doctors/${doctor.img_main}`);
      }
    }
  }, [doctor, form]);

  const beforeUpload = (file) => {
    const isImage =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/jpg";

    if (!isImage) {
      message.error("Only JPG/PNG images allowed.");
      return Upload.LIST_IGNORE;
    }

    setPreviewUrl(URL.createObjectURL(file));
    return false;
  };

  const handleImageChange = ({ fileList }) => {
    setFileList(fileList);
  };

  const handleSubmit = async (values) => {
    const formData = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      if (key === "birthDate") {
        formData.append(key, value.format("YYYY-MM-DD"));
      } else {
        formData.append(key, value);
      }
    });

    if (fileList.length > 0 && fileList[0].originFileObj) {
      formData.append("img_main", fileList[0].originFileObj);
    }

    try {
      if (mode === "add") {
        await createDoctor(formData);
        message.success("Doctor created!");
      } else {
        await updateDoctor(doctor.id, formData);
        message.success("Doctor updated!");
      }

      form.resetFields();
      setFileList([]);
      setPreviewUrl(null);
      onSuccess?.();
    } catch (err) {
      console.error(err);
      message.error("Error saving doctor.");
    }
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={handleSubmit}
      initialValues={{
        categoryId: doctor?.categoryId || undefined,
      }}
    >
      <h3>{mode === "add" ? "Ավելացնել բժիշկ" : "Թարմացնել բժիշկ"}</h3>

      {/* Names */}
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="firstNameAM" label="Անուն (Հայերեն)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="lastNameAM" label="Ազգանուն (Հայերեն)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="fatherNameAM" label="Հայրանուն (Հայերեն)">
            <Input />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item name="firstNameEN" label="Անուն (Անգլերեն)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="lastNameEN" label="Ազգանուն (Անգլերեն)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="fatherNameEN" label="Հայրանուն (Անգլերեն)">
            <Input />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item name="firstNameRU" label="Անուն (Ռուսերեն)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="lastNameRU" label="Ազգանուն (Ռուսերեն)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="fatherNameRU" label="Հայրանուն (Ռուսերեն)">
            <Input />
          </Form.Item>
        </Col>
      </Row>

      {/* Contacts */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="phone" label="Հեռախոս" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Էլ. փոստ"
            rules={[
              { required: true },
              { type: "email", message: "Invalid email" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="birthDate" label="Ծննդյան ամսաթիվ" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="cabinet" label="Սենյակ">
            <Input />
          </Form.Item>

          {/* Department */}
          <Form.Item
            name="categoryId"
            label="Բաժին"
            rules={[{ required: true }]}
          >
            <Select placeholder="Ընտրեք բաժինը">
              {categories.map((c) => (
                <Option key={c.id} value={c.id}>
                  {c.nameAM}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Image */}
          <Form.Item label="Նկար">
            <Upload
              fileList={fileList}
              beforeUpload={beforeUpload}
              onChange={handleImageChange}
              listType="picture"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Բեռնել Նկար</Button>
            </Upload>
            {previewUrl && (
              <img
                src={previewUrl}
                alt="preview"
                style={{
                  width: 140,
                  marginTop: 10,
                  borderRadius: 8,
                  border: "1px solid #ddd",
                }}
              />
            )}
          </Form.Item>
        </Col>

        {/* Armenian medical */}
        <Col span={4}>
          <Form.Item name="positionAM" label="Պաշտոն Հայերեն">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="scientificDegreeAM" label="Աստիճան Հայերեն">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="articlesAM" label="Հոդվածներ Հայերեն">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="membershipsAM" label="Անդամություններ Հայերեն">
            <TextArea rows={2} />
          </Form.Item>
        </Col>

        {/* English medical */}
        <Col span={4}>
          <Form.Item name="positionEN" label="Պաշտոն Անգլերեն">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="scientificDegreeEN" label="Գիտական Աստիճան Անգլերեն">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="articlesEN" label="Հոդվածներ Անգլերեն">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="membershipsEN" label="Անդամություններ Անգլերեն">
            <TextArea rows={2} />
          </Form.Item>
        </Col>

        {/* Russian medical */}
        <Col span={4}>
          <Form.Item name="positionRU" label="Պաշտոն Ռուսերեն">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="scientificDegreeRU" label="Գիտական Աստիճան Ռուսերեն">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="articlesRU" label="Հոդվածներ Ռուսերեն">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="membershipsRU" label="Անդամություններ Ռուսերեն">
            <TextArea rows={2} />
          </Form.Item>
        </Col>
      </Row>

      {/* Experience */}
      <Row>
        <Col span={24}>
          <Form.Item name="workingExperience" label="Աշխատանքային փորձ">
            <TextArea rows={3} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item>
        <Button type="primary" htmlType="submit" block>
          {mode === "add" ? "Ստեղծել բժիշկ" : "Թարմացնել բժիշկ"}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default DoctorForm;
